import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { TimezoneSelector } from './ui/TimezoneSelector';
import { X, ChevronRight, ChevronLeft, Rocket, Mail, BarChart3, Users, Settings, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api/api';

const TOUR_STEPS = [
  {
    title: 'Hoşgeldiniz!',
    description: 'Mailwise ile e-posta pazarlama dünyasına hoşgeldiniz. İşte bilmeniz gerekenler:',
    icon: Rocket,
    color: 'from-blue-500 to-cyan-500',
    features: [
      { icon: Mail, title: 'Kampanya Yönetimi', desc: 'E-posta kampanyalarınızı oluşturun ve yönetin' },
      { icon: BarChart3, title: 'Raporlama', desc: 'Detaylı analitikler ve raporlar' },
      { icon: Users, title: 'Kişi Yönetimi', desc: 'Kişilerinizi gruplar ve listeler halinde yönetin' },
      { icon: Settings, title: 'Ayarlar', desc: 'Hesap ayarlarınızı ve tercihlerinizi yapılandırın' },
    ],
  },
  {
    title: 'Kampanyalar',
    description: 'Kampanya sekmesinden yeni bir e-posta kampanyası oluşturabilir, şablonları kullanabilir ve gönderim zamanlamasını ayarlayabilirsiniz.',
    icon: Mail,
    color: 'from-violet-500 to-purple-500',
  },
  {
    title: 'Kişiler & Gruplar',
    description: 'Kişilerinizi içe aktarın, gruplar oluşturun ve hedeflenmiş kampanyalar hazırlayın.',
    icon: Users,
    color: 'from-green-500 to-emerald-500',
  },
  {
    title: 'Raporlar & Analitikler',
    description: 'Gönderim başarısını, açılma oranlarını ve tıklama istatistiklerini takip edin.',
    icon: BarChart3,
    color: 'from-orange-500 to-amber-500',
  },
  {
    title: 'Hazırsınız!',
    description: 'Tüm ayarları tamamladık. Şimdi Mailwise özelliklerini keşfetmeye başlayabilirsiniz.',
    icon: Sparkles,
    color: 'from-pink-500 to-rose-500',
  },
];

export default function OnboardingModal({ onComplete }) {
  const [step, setStep] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  );
  const [saving, setSaving] = useState(false);
  const { user } = useSelector((state) => state.auth);

  const currentTourStep = TOUR_STEPS[step];
  const isLastTourStep = step === TOUR_STEPS.length - 1;

  const handleNext = () => {
    if (isLastTourStep) {
      setShowForm(true);
    } else {
      setStep((s) => s + 1);
    }
  };

  const handleBack = () => {
    if (showForm) {
      setShowForm(false);
      setStep(TOUR_STEPS.length - 1);
    } else if (step > 0) {
      setStep((s) => s - 1);
    }
  };

  const handleSkip = () => {
    completeOnboarding();
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/user/me', {
        name: name || undefined,
        company,
        timezone,
      });
      toast.success('Bilgileriniz kaydedildi!');
      completeOnboarding();
    } catch (err) {
      toast.error('Bilgiler kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSaving(false);
    }
  };

  const completeOnboarding = () => {
    localStorage.setItem('onboardingComplete', 'true');
    if (onComplete) onComplete();
  };

  if (showForm) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="w-full max-w-lg bg-card border border-border/50 rounded-3xl shadow-2xl p-8 mx-4 animate-in fade-in zoom-in duration-300">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-2xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg">
                <Sparkles className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">Bilgilerinizi Tamamlayın</h2>
                <p className="text-sm text-muted-foreground">Size daha iyi hizmet verebilmemiz için bilgilerinizi girin</p>
              </div>
            </div>
            <button
              onClick={handleSkip}
              className="text-muted-foreground hover:text-foreground transition-colors p-1"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleFormSubmit} className="grid gap-5">
            <div className="grid gap-2">
              <Label htmlFor="onboarding-name" className="text-sm font-semibold">
                Adınız / İletişim Kişisi
              </Label>
              <Input
                id="onboarding-name"
                type="text"
                placeholder={user?.name || 'Ömer Faruk Yılmaz'}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-muted/30 border-2 border-border/50 rounded-2xl focus:border-primary py-3.5 px-5 text-sm"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="onboarding-company" className="text-sm font-semibold">
                Şirket Adı
              </Label>
              <Input
                id="onboarding-company"
                type="text"
                placeholder="Mailwise Ltd."
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className="w-full bg-muted/30 border-2 border-border/50 rounded-2xl focus:border-primary py-3.5 px-5 text-sm"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="onboarding-timezone" className="text-sm font-semibold">
                Zaman Dilimi
              </Label>
              <TimezoneSelector value={timezone} onChange={setTimezone} />
              <p className="text-xs text-muted-foreground">
                Kampanya zamanlamaları bu zaman dilimine göre ayarlanacaktır.
              </p>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-border/50">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                className="rounded-2xl px-6 py-2.5"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Geri
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="rounded-2xl px-8 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0 shadow-lg"
              >
                {saving ? 'Kaydediliyor...' : 'Tamamla'}
                {!saving && <ChevronRight className="h-4 w-4 ml-2" />}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-card border border-border/50 rounded-3xl shadow-2xl overflow-hidden mx-4 animate-in fade-in zoom-in duration-300">
        {/* Progress bar */}
        <div className="h-1.5 bg-muted/30">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-500 ease-out"
            style={{ width: `${((step + 1) / TOUR_STEPS.length) * 100}%` }}
          />
        </div>

        <div className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-2xl bg-gradient-to-r ${currentTourStep.color} text-white shadow-lg`}>
                <currentTourStep.icon className="h-7 w-7" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-foreground">{currentTourStep.title}</h2>
                <p className="text-sm text-muted-foreground mt-1">{currentTourStep.description}</p>
              </div>
            </div>
            <button
              onClick={handleSkip}
              className="text-muted-foreground hover:text-foreground transition-colors p-1"
              title="Atla"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Features grid for welcome step */}
          {currentTourStep.features && (
            <div className="grid grid-cols-2 gap-4 mb-8">
              {currentTourStep.features.map((feature, idx) => (
                <div
                  key={idx}
                  className="p-5 rounded-2xl bg-muted/30 border border-border/50 hover:border-primary/30 transition-colors"
                >
                  <div className="p-2.5 rounded-xl bg-background w-fit mb-3 shadow-sm">
                    <feature.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="text-sm font-bold text-foreground mb-1">{feature.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          )}

          {/* Step content for non-welcome steps */}
          {!currentTourStep.features && (
            <div className="mb-8 p-6 rounded-2xl bg-muted/30 border border-border/50">
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-2 rounded-xl bg-gradient-to-r ${currentTourStep.color} text-white`}>
                  <currentTourStep.icon className="h-5 w-5" />
                </div>
                <span className="text-sm font-semibold text-foreground">{currentTourStep.title}</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{currentTourStep.description}</p>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between pt-4 border-t border-border/50">
            <Button
              type="button"
              variant="ghost"
              onClick={handleSkip}
              className="text-muted-foreground hover:text-foreground text-sm"
            >
              Atla
            </Button>
            <div className="flex items-center gap-4">
              {step > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep((s) => s - 1)}
                  className="rounded-2xl px-5 py-2.5"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Geri
                </Button>
              )}
              <Button
                type="button"
                onClick={handleNext}
                className="rounded-2xl px-6 py-2.5 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0 shadow-lg"
              >
                {isLastTourStep ? (
                  <>
                    Devam Et
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </>
                ) : (
                  <>
                    İleri
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Dots indicator */}
          <div className="flex items-center justify-center gap-2 mt-6">
            {TOUR_STEPS.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setStep(idx)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  idx === step ? 'w-6 bg-primary' : 'w-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
